import { ModalProps } from "antd";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { Button, FlexColumn, message, Modal, TextArea, Typography } from "../../../components";
import { CoefficientIcon } from "../../../components/Icons/logos";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { useTrack } from "../../../utils";
import { submitContactSalesForm } from "../billing.utils";
import type { AiDashboardsGateProps } from "../planChooserSource.utils";

type Props = Omit<ModalProps, "onCancel"> & {
  showModal?: boolean;
  onCancel?: (e?: React.MouseEvent<HTMLElement>) => void;
  description?: string;
  hideTextarea?: boolean;
  // Feature-gate tags for the submit event, so a request from a gate's contact-sales path records
  // as contact_sales in the shared gate funnel (ENG-9327).
  gateProps?: AiDashboardsGateProps;
};

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.3335 6.00008L2.50016 4.16675L0.666829 3.33341L2.50016 2.50008L3.3335 0.666748L4.16683 2.50008L6.00016 3.33341L4.16683 4.16675L3.3335 6.00008ZM8.3335 6.33342L10.0002 2.66675L11.6668 6.33342L15.3335 8.00008L11.6668 9.66675L10.0002 13.3334L8.3335 9.66675L4.66683 8.00008L8.3335 6.33342ZM3.3335 10.0001L4.16683 11.8334L6.00016 12.6667L4.16683 13.5001L3.3335 15.3334L2.50016 13.5001L0.666829 12.6667L2.50016 11.8334L3.3335 10.0001Z"
      fill="#3053FF"
    />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="10" fill="#22c55e" />
    <path
      d="M6 10.5l3 3 5-6"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ContactSalesModal: React.FC<Props> = ({
  showModal,
  description,
  hideTextarea,
  gateProps,
  ...modalProps
}) => {
  const track = useTrack();
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const [textValue, setTextValue] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRequestSent, setIsRequestSent] = React.useState(false);

  useEffect(() => {
    if (!showModal) {
      setIsRequestSent(false);
      setTextValue("");
    }
  }, [showModal]);

  const handleContactSales = async () => {
    track("contact_sales_form_submitted", { event_from: "contact_sales_modal", ...gateProps });
    setIsSubmitting(true);
    const isMessageSent = await submitContactSalesForm(
      hideTextarea
        ? description ?? "Interested in adding more seats with AI Dashboards"
        : textValue,
      clientFlags
    );
    setIsSubmitting(false);
    if (isMessageSent) {
      if (hideTextarea) {
        setIsRequestSent(true);
      } else {
        message.success("Message sent successfully");
        setTextValue("");
        modalProps.onCancel?.();
      }
    } else {
      message.error("Failed to send message, please contact support");
    }
  };

  // Branded variant: gradient header + logo + avatar + success state.
  // Mirrors the pattern used in WebDashboardUpgradeModal.
  if (hideTextarea) {
    if (!showModal) {
      return null;
    }

    return (
      <div
        onClick={modalProps.onCancel as React.MouseEventHandler}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.45)",
          padding: "20px",
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 380,
            overflow: "hidden",
            borderRadius: 16,
            background: "white",
            boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
          }}
        >
          {!isRequestSent ? (
            <>
              {/* Gradient header */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "32px 0",
                  background: "linear-gradient(135deg, #5b2de0 0%, #e8502d 100%)",
                }}
              >
                <CoefficientIcon fill="white" style={{ width: 60, height: 60 }} />
                <button
                  onClick={modalProps.onCancel as React.MouseEventHandler}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    cursor: "pointer",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: 24 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 17, fontWeight: 700, color: "#111215" }}>
                    Contact Sales
                  </span>
                  <SparkleIcon />
                </div>
                <p
                  style={{
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: "#444",
                    margin: "0 0 20px",
                  }}
                >
                  {description ??
                    "We'll reach out with more information about upgrading your plan."}
                </p>
                <button
                  onClick={handleContactSales}
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    padding: "12px 0",
                    borderRadius: 6,
                    background: "#3053ff",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    border: "none",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                >
                  {isSubmitting ? "Sending..." : "Contact Sales"}
                </button>
              </div>
            </>
          ) : (
            /* Success state */
            <div style={{ padding: 24 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckCircleIcon />
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#111215" }}>
                    Request Sent
                  </span>
                </div>
                <button
                  onClick={modalProps.onCancel as React.MouseEventHandler}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#666",
                    display: "flex",
                    alignItems: "center",
                    padding: 0,
                  }}
                >
                  <CloseIcon />
                </button>
              </div>
              <p
                style={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "#666",
                  margin: "0 0 20px",
                }}
              >
                Our sales team will reach out with more information about upgrading your plan.
              </p>
              <button
                onClick={modalProps.onCancel as React.MouseEventHandler}
                style={{
                  width: "100%",
                  padding: "12px 0",
                  borderRadius: 6,
                  background: "#3053ff",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: existing Ant Design modal with textarea.
  return (
    <Modal
      {...modalProps}
      width={450}
      padding={"20px"}
      centered={true}
      visible={showModal}
      closable={true}
    >
      <FlexColumn gap={2}>
        <Typography variant={"h6"} fontWeight={"bold"} style={{ marginBottom: "2px" }}>
          Contact Sales
        </Typography>
        <Typography variant={"body2"} style={{ color: COLORS.black50 }}>
          {description ?? "We'll reach out with more information about upgrading your plan."}
        </Typography>
        <TextArea
          placeholder={"Any questions?"}
          style={{ height: "100px" }}
          value={textValue}
          onChange={e => setTextValue(e.target.value)}
        ></TextArea>
        <FlexColumn alignItems={"flex-end"}>
          <Button type={"primary"} onClick={handleContactSales} style={{ width: 110 }}>
            Contact Sales
          </Button>
        </FlexColumn>
      </FlexColumn>
    </Modal>
  );
};
